<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <h4 class="mb-0">
          <i class="material-icons text-danger me-2"></i> Edit Salary
        </h4>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="row">
      <div class="col-12 text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x" />
        <p class="mt-2">Loading salary...</p>
      </div>
    </div>

    <!-- Form only -->
    <div v-else class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-3 position-relative mt-n4 mx-3 z-index-2" style="background-color:#b03a45;border-radius:12px;">
            <h6 class="text-white text-capitalize mb-0">Salary Information</h6>
          </div>

          <div class="card-body px-4 pt-4 pb-3">
            <form @submit.prevent="submitForm">
              <div class="row">
                <!-- Title -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">{{ $t('common.labels.title') }}</label>
                  <input v-model="form.title" type="text" class="form-control" :placeholder="$t('common.placeholders.enterSalaryTitle')" required />
                </div>

                <!-- Date (type=date) -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Date</label>
                  <input v-model="form.date_raw" type="date" class="form-control" required />
                </div>
              </div>

              <!-- Active switch -->
              <div class="row mb-3">
                <div class="col-12">
                  <label class="form-label d-block mb-2">Status</label>
                  <div class="form-check form-switch">
                    <input id="active" v-model="form.active" class="form-check-input" type="checkbox" role="switch" style="cursor:pointer;accent-color:#b03a45;" />
                    <label class="form-check-label" for="active">{{ form.active ? 'Active' : 'Inactive' }}</label>
                  </div>
                </div>
              </div>

              <!-- Current document + upload new (optional) -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Current document</label>
                  <div>
                    <a v-if="form.document_url" class="btn btn-outline-dark btn-sm" :href="form.document_url" target="_blank">
                      <i class="fas fa-download me-1"></i> Download current
                    </a>
                    <span v-else class="text-muted small">— no document —</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Replace document (optional)</label>
                  <input ref="fileInputRef" @change="onFileChange" type="file" class="form-control" accept=".pdf,.doc,.docx,.xls,.xlsx" />
                  <small class="text-muted">Max 10 MB. Formats: pdf, doc, docx, xls, xlsx.</small>
                  <div v-if="form.document_name" class="mt-1 small">
                    Selected: <strong>{{ form.document_name }}</strong>
                    <button type="button" class="btn btn-link btn-sm" @click="clearFile">Remove</button>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="row mt-4">
                <div class="col-12 text-end">
                  <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm" :disabled="isSubmitting">RESET</button>
                  <button type="submit" class="btn text-white" style="background-color:#b03a45;" :disabled="isSubmitting">
                    <span v-if="isSubmitting"><i class="fas fa-spinner fa-spin me-1" /> Updating...</span>
                    <span v-else>UPDATE SALARY</span>
                  </button>
                </div>
              </div>
            </form>

            <div v-if="lastError" class="alert alert-danger mt-3" role="alert">{{ lastError }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const LIST_ROUTE = '/salary'
const ENDPOINT = '/financial-requests/salaries'

const isSubmitting = ref(false)
const loading = ref(true)
const lastError = ref('')
const fileInputRef = ref(null)

const salaryId = ref(String(route.params.id || ''))

// Form (date_raw = "YYYY-MM-DD")
const form = ref({
  title: '',
  date_raw: '',
  active: true,
  document_url: null,
  document_file: null,
  document_name: ''
})

// Snapshot pour RESET
const original = ref(null)

/* Helpers de conversion */
const mmYYYY_to_yyyyMMdd = (mmYYYY) => {
  if (!mmYYYY) return ''
  const [mm, yyyy] = mmYYYY.split('-')
  return `${yyyy}-${mm}-01`
}
const yyyyMMdd_to_mmYYYY = (yyyyMMdd) => {
  if (!yyyyMMdd) return ''
  const [yyyy, mm] = yyyyMMdd.split('-')
  return `${mm}-${yyyy}`
}
const asBool = (v) => v === true || v === 1 || v === '1'

/* URL document à partir d'axios.baseURL si pas d'URL absolue */
const buildStorageUrl = (p) => {
  if (!p) return null
  try {
    const base = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '') || 'http://127.0.0.1:8000'
    return `${base}/storage/${p}`
  } catch {
    return null
  }
}

/* Pré-remplissage - essai direct /:id puis fallback via index paginé */
const fetchSalary = async () => {
  loading.value = true
  lastError.value = ''
  try {
    // Essai : show (si jamais présent côté API)
    const { data } = await axios.get(`${ENDPOINT}/${salaryId.value}`)
    const s = data?.data || data
    applySalary(s)
  } catch (e) {
    // Fallback : parcourir l'index paginé jusqu'à trouver l'ID
    try {
      let page = 1
      while (true) {
        const { data } = await axios.get(ENDPOINT, { params: { page } })
        const p = data?.salaries || {}
        const rows = Array.isArray(p.data) ? p.data : []
        const found = rows.find(r => String(r.id) === salaryId.value)
        if (found) { applySalary(found); break }
        if (!p.next_page_url && (p.current_page >= p.last_page)) {
          throw new Error('Item not found in any page')
        }
        page = (p.current_page || page) + 1
      }
    } catch (err) {
      console.error('Error loading salary:', err)
      lastError.value = 'Failed to load salary details from API.'
    }
  } finally {
    loading.value = false
  }
}

function applySalary(item) {
  if (!item) return
  form.value.title = item.title || ''
  form.value.date_raw = mmYYYY_to_yyyyMMdd(item.date || '')
  form.value.active = asBool(item.active)
  form.value.document_url = item.document_url || buildStorageUrl(item.document_path)
  form.value.document_file = null
  form.value.document_name = ''
  original.value = { ...form.value }
}

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) { clearFile(); return }
  const allowed = ['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
  const tooBig = file.size > 10 * 1024 * 1024
  if (!allowed.includes(file.type) || tooBig) {
    toast.error('Invalid file. Use pdf/doc/docx/xls/xlsx up to 10MB.')
    clearFile(); return
  }
  form.value.document_file = file
  form.value.document_name = file.name
}
function clearFile() {
  form.value.document_file = null
  form.value.document_name = ''
  if (fileInputRef.value) fileInputRef.value.value = ''
}

/* Update */
const submitForm = async () => {
  if (!form.value.title.trim()) { toast.error('Title is required'); return }
  if (!form.value.date_raw) { toast.error('Please select a date'); return }

  // Utilise multipart + override _method=PUT pour Laravel
  const fd = new FormData()
  fd.append('_method', 'PUT')
  fd.append('title', form.value.title)
  fd.append('date', yyyyMMdd_to_mmYYYY(form.value.date_raw))
  fd.append('active', form.value.active ? '1' : '0')
  if (form.value.document_file) fd.append('document_path', form.value.document_file)

  isSubmitting.value = true
  try {
    await axios.post(`${ENDPOINT}/${salaryId.value}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    toast.success('Salary updated successfully')
    router.push(LIST_ROUTE)
  } catch (err) {
    console.error('Error updating salary:', err)
    const apiMsg = err?.response?.data?.message
    const laravelErrors = err?.response?.data?.errors
    if (laravelErrors && typeof laravelErrors === 'object') {
      const firstField = Object.keys(laravelErrors)[0]
      const firstMsg = laravelErrors[firstField]?.[0]
      toast.error(firstMsg || apiMsg || 'Failed to update salary')
    } else {
      toast.error(apiMsg || 'Failed to update salary')
    }
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  if (original.value) {
    form.value = { ...original.value }
    if (fileInputRef.value) fileInputRef.value.value = ''
  }
}

watch(() => route.params.id, (newId) => { salaryId.value = String(newId || ''); fetchSalary() })

onMounted(fetchSalary)
</script>

<style scoped>
.form-check-input:checked { background-color:#b03a45; border-color:#b03a45; }
</style>
